// 개발요청 — 표시 문자·배지 색·날짜·사용 위치 링크 판정(D4a). 화면 두 곳(목록·상세)이 같이 쓴다.
// 🔴 권한은 여기서 계산하지 않는다 — 단추를 보일지는 서버가 준 item.can.* 으로만 정한다.
import { COLORS, TONES } from '../../utils/uiStyles';

// 회차 상태(서버 devreq_rounds.status) → 한국어.
export const ROUND_STATUS_LABELS = { draft: '준비', open: '접수 중', review: '검토', closed: '종료' };
const ROUND_STATUS_COLORS = {
  draft: [TONES.gray.soft, TONES.gray.ink],
  open: [COLORS.successSoft, COLORS.successText],
  review: [COLORS.warnSoft, COLORS.warn],
  closed: [TONES.gray.soft, TONES.gray.ink],
};

// 상태·진행 여부 — 서버 devreq_rules.STATUSES · DECISIONS 와 같은 글자.
export const STATUSES = ['접수', '검토', '대기', '개발 중', '테스트', '완료'];
export const DECISIONS = ['진행', '보류', '반려', '통합'];
// 검토 칸(D5a) — 서버 devreq_rules.PROCEED_STATUSES(진행으로 정한 요청만 가는 상태) · HOLD_DECISIONS(사유가 필요한 결정) · DETAIL_STATES.
export const PROCEED_STATUSES = ['대기', '개발 중', '테스트', '완료'];
export const HOLD_DECISIONS = ['보류', '반려', '통합'];
export const DETAIL_STATES = ['요청 전', '요청함', '받음'];
const STATUS_COLORS = {
  접수: [TONES.gray.soft, TONES.gray.ink],
  검토: [COLORS.warnSoft, COLORS.warn],
  대기: [COLORS.infoSoft, COLORS.info],
  '개발 중': [COLORS.primarySoft, COLORS.primaryDark],
  테스트: [TONES.purple.soft, TONES.purple.ink],
  완료: [COLORS.successSoft, COLORS.successText],
};
const DECISION_COLORS = {
  진행: [COLORS.successSoft, COLORS.successText],
  보류: [COLORS.warnSoft, COLORS.warn],
  반려: [COLORS.dangerSoft, COLORS.dangerText],
  통합: [COLORS.infoSoft, COLORS.info],
};
const NEUTRAL = [TONES.gray.soft, TONES.gray.ink];

// 비공개 가림 문구 — 서버 devreq_rules.MASKED_TITLE 과 같은 글자.
export const MASKED_TITLE = '비공개 요청';
export const MASKED_NOTICE = '비공개 요청입니다 — 요청 부서와 AX만 내용을 봅니다';
export const EMPTY = '—';

export const roundStatusLabel = (status) => ROUND_STATUS_LABELS[status] || status || EMPTY;

export function badge([background, color]) {
  return {
    display: 'inline-block', padding: '1px 6px', borderRadius: '20px', fontSize: '11px',
    fontWeight: 500, whiteSpace: 'nowrap', background, color,
  };
}
export const roundStatusBadge = (status) => badge(ROUND_STATUS_COLORS[status] || NEUTRAL);
export const statusBadge = (status) => badge(STATUS_COLORS[status] || NEUTRAL);
export const decisionBadge = (decision) => badge(DECISION_COLORS[decision] || NEUTRAL);

// 빈 값은 —.
export const text = (value) => (value === null || value === undefined || value === '' ? EMPTY : String(value));

// 날짜 칸('YYYY-MM-DD')은 앞 10자만 — 날짜 전용 칸이라 시간대 변환을 하지 않는다. 빈 값·깨진 값은 —.
export function fmtDay(value) {
  if (typeof value !== 'string') return EMPTY;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return m ? m[1] : EMPTY;
}

// 회차 이름 — «3회차» 또는 «3회차 · 제목».
export function roundName(round) {
  if (!round) return EMPTY;
  const no = round.round_no !== undefined && round.round_no !== null ? `${round.round_no}회차` : '';
  return round.title ? `${no} · ${round.title}` : (no || EMPTY);
}

// 사용 위치 — 서버 devreq_rules 의 _HTTPS_RE · _APP_PATH_RE 와 같은 정규식(NotificationCenter safeLink 와 같은 앱 안 경로).
//   https:// → 새 탭 · 앱 안 경로 → 같은 탭 · 그 밖(javascript: 등) → 링크로 만들지 않는다(글자).
const HTTPS_RE = /^https:\/\/[^\s/\\]\S*$/;
const APP_PATH_RE = /^\/(?![/\\])\S*$/;
export function usageLinkKind(value) {
  if (typeof value !== 'string' || !value || value.length > 300) return 'text';
  if (HTTPS_RE.test(value)) return 'external';
  if (APP_PATH_RE.test(value)) return 'internal';
  return 'text';
}

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(contractLabels 와 같다).
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

// 🔴 쓰기 화면(D4b)의 실패 문구는 이것을 거친다 — contractLabels.errorText 와 같은 꼴.
//    responseType 'blob' 요청(첨부 내려받기)은 오류 본문도 Blob 이라 본문을 읽어 detail 을 꺼낸다.
//    detail 이 글자가 아니면(422 는 객체 목록 · nginx HTML) 대체 문구 — 그대로 그리면 React 가 깨진다.
export async function errorText(err, fallback) {
  const data = err?.response?.data;
  let detail;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const body = await readBlobText(data);
      try { detail = JSON.parse(body)?.detail; } catch { /* 파싱 실패면 대체 문구 */ }
    } catch { /* 읽기 실패면 대체 문구 */ }
  } else {
    detail = data?.detail;
  }
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
}

// AI 1차 판독(P1b · 서버 devreq_requests.ai_screen_state) → 문구. 서버는 AX 에게만 값을 싣는다(아니면 null).
export const AI_SCREEN_LABELS = {
  needs: 'AI 보강 요청 중', hold: 'AI: 사람 판단 필요', error: 'AI 처리 실패', ok: 'AI 통과', rejected: 'AI 자동 반려',
};
const AI_SCREEN_COLORS = {
  needs: [COLORS.warnSoft, COLORS.warn],
  hold: [COLORS.warnSoft, COLORS.warn],
  error: [TONES.red.soft, TONES.red.ink],
  ok: NEUTRAL,
  rejected: NEUTRAL,
};
// 사람이 봐야 하는 판독(주의) — 사람이 진행 여부를 정했거나 철회한 요청에는 배지로 남기지 않는다.
const AI_SCREEN_ALERTS = ['needs', 'hold', 'error'];
// AI 시스템 계정 이름은 화면에 쓰지 않는다 — 서버 표식(추가 요청 by_ai · 이력 actor_is_ai)을 보고 이 글자로 그린다.
export const AI_REVIEWER = 'AI 검토';
export const ASKED_BY_REVIEWER = '검토자가 추가로 요청했어요';
export const ASKED_BY_AI = 'AI 검토가 추가로 요청했어요';
// 판독 상태 → { state, label, colors, muted } 또는 null(값 없음·모르는 값 · 자동 반려인데 결정이 '반려' 가 아님).
//   muted = 주의 상태인데 이미 정했거나 철회한 요청 — 목록은 배지를 안 그리고 상세는 회색 글자로.
//   «AI 자동 반려»는 결정이 '반려'(P1b 가 넣는 값)일 때만 — AX 가 «진행»으로 뒤집으면 보이지 않는다.
export function aiScreenView(item) {
  const state = item?.ai_screen_state;
  if (!Object.prototype.hasOwnProperty.call(AI_SCREEN_LABELS, state ?? '')) return null;
  if (state === 'rejected' && item.decision !== '반려') return null;
  return {
    state,
    label: AI_SCREEN_LABELS[state],
    colors: AI_SCREEN_COLORS[state],
    muted: AI_SCREEN_ALERTS.includes(state) && (!!item.decision || item.withdrawn === true),
  };
}

// AI 종합 판단(P2b · 서버 GET /requests/{id}/ai-report · AX 만) — 상세 ② 카드의 묶음(DevRequestAiReport) 문구.
// 🔴 약속하지 않는다(«만드는 중»·«다시 시도» 없음) — 회차 «AI 선별»이 꺼지면 서버는 판단을 안 만드는데 상세 응답엔 그걸 가를 키가 없다.
export const AI_REPORT_TEXT = {
  title: 'AI 종합 판단',
  none: '아직 AI 종합 판단이 없어요',
  stale: '요청·답·기준이 바뀌기 전의 판단이에요',
  error: 'AI 종합 판단을 만들지 못했어요',
  loadFailed: 'AI 종합 판단을 불러오지 못했어요',
  recommend: 'AI 권고',
  priority: 'AI 우선순위',
  reasons: '근거',
  concerns: '걱정거리',
  mergeWith: '묶을 만한 요청',
  at: 'AI 판단',
};
// 판단 뒤 같은 회차 요청의 차 — 서버 siblings_added·siblings_removed(정수 · 보고 없으면 0).
export const aiReportSiblingsText = (added, removed) => `이 판단 뒤 같은 회차 요청이 ${added}건 늘고 ${removed}건 줄었어요.`;

// 이력 action → 한국어(모르는 글자는 그대로).
const EVENT_ACTIONS = {
  create: '작성', override: '상한 초과 작성', update: '수정', rank: '순위', withdraw: '철회',
  decide: '진행 여부', merge: '통합', status: '상태', visibility: '공개 범위', confirm: '완료 확인',
  carry: '이월', file: '첨부', comment: '메모', comment_internal: '내부 메모', ask: '추가 요청',
  ai_screen: 'AI 1차 판독',
};
// 추가 요청(2판 V3)은 field 로 무엇을 했는지 가른다 — 다른 action 은 field 를 보지 않는다.
const ASK_EVENT_ACTIONS = {
  add: '추가 요청 보냄', answer: '추가 요청에 답함', cancel: '추가 요청 거둠', remind: '추가 요청 다시 알림',
};
// AI 1차 판독 줄(AX 만 받는다) — 칸 이름 대신 «판독 결과» · 값은 판독 문구로.
const AI_SCREEN_FIELD = 'ai_screen_state';
const AI_SCREEN_FIELD_LABEL = '판독 결과';
const aiScreenValue = (value) => AI_SCREEN_LABELS[value] || text(value);
// DEV 확인 계정(서버 action qa_account)도 field 로 가른다 — 모르는 field 는 action 글자 그대로.
const QA_EVENT_ACTIONS = {
  issue: 'DEV 확인 계정 발급', reissue: 'DEV 확인 계정 재발급', revoke: 'DEV 확인 계정 회수', dm: 'DEV 확인 계정 메신저 보냄',
};
// 요청자 확인 칸(서버 devreq_rules.CONFIRM_FIELDS) — 이력에는 서버 칸 이름 대신 이 이름으로 보인다.
const CONFIRM_FIELD_LABELS = {
  confirm_state: '확인 결과', confirm_note: '확인 메모', confirmed_by: '확인한 사람', confirmed_at: '확인 시각',
};
// 확인한 사람(사용자 번호)·확인 시각(서버 원 글자)은 값 대신 이름만 보인다.
const CONFIRM_NO_VALUE_FIELDS = ['confirmed_by', 'confirmed_at'];
// 서버는 확인 칸을 «update» 로 쓰는 곳이 상태 변경 자동 비움(devreq_rules.py 상태 바뀌면 확인 칸 비움) 하나뿐이다
// → 그 줄은 «수정»이 아니라 «상태 변경으로 요청자 확인 비움»이다(상태 바꾼 사람이 그 글을 쓴 것이 아니다 · #1929).
// Object.hasOwn 은 쓰지 않는다(이 레포 frontend/src 사용 0건 · 폴리필 없음 · browserslist 에 옛 Safari 포함).
const isConfirmField = (field) => Object.prototype.hasOwnProperty.call(CONFIRM_FIELD_LABELS, field ?? '');
export const isConfirmClearEvent = (ev) => ev?.action === 'update' && isConfirmField(ev?.field);
export const CONFIRM_CLEAR_LABEL = '상태 변경으로 요청자 확인 비움';

// 개발 시작일(dev_started_on) — 서버가 상태를 바꾼 검토 안에서 자동으로 정하는 칸이라(services/devreq_rules.py · action update)
// 상태를 바꾼 사람의 «수정» 줄로 보이지 않게 자체 문구를 쓴다: 처음 기록(옛 값 없음) = «개발 시작일 기록» ·
// 완료일을 시작일보다 앞당겨 맞춰 내림(옛 값 있음) = «개발 시작일 조정(완료일에 맞춤)».
// 🔴 기록/조정은 옛 값으로 가른다 — 상세(DevRequestDetail)는 라벨을 eventActionLabel(action, field) 로만 부르므로 라벨은
//    «개발 시작일» 까지이고 뒤의 « 기록»·« 조정(완료일에 맞춤)» 은 historyDetail 이 붙인다(상세가 둘을 이어 그린다).
const DEV_STARTED_FIELD = 'dev_started_on';
export const DEV_STARTED_LABEL = '개발 시작일';
const DEV_STARTED_RECORDED_TAIL = ' 기록';
const DEV_STARTED_ADJUSTED_TAIL = ' 조정(완료일에 맞춤)';
export const DEV_STARTED_RECORDED = `${DEV_STARTED_LABEL}${DEV_STARTED_RECORDED_TAIL}`;
export const DEV_STARTED_ADJUSTED = `${DEV_STARTED_LABEL}${DEV_STARTED_ADJUSTED_TAIL}`;
const isDevStartedEvent = (ev) => ev?.action === 'update' && ev?.field === DEV_STARTED_FIELD;

export const eventActionLabel = (action, field) => (action === 'update' && isConfirmField(field) && CONFIRM_CLEAR_LABEL)
  || (action === 'update' && field === DEV_STARTED_FIELD && DEV_STARTED_LABEL)
  || (action === 'ask' && ASK_EVENT_ACTIONS[field])
  || (action === 'qa_account' && QA_EVENT_ACTIONS[field])
  || EVENT_ACTIONS[action] || action || EMPTY;

// 이력 한 줄의 « · 칸 : 전 → 후» 부분(줄 머리 «시각 · 사람 · 무엇» 뒤). events 는 서버 순서(새것부터) 그대로.
// 추가 요청·DEV 확인 계정은 라벨이 이미 무엇을 했는지 말해 칸을 붙이지 않고, DEV 확인 계정의 «rev N» 은 내부 글자라 값도 보이지 않는다.
// 확인 칸이면 한글 이름 · 비움 줄은 그 값을 쓴 사람(더 오래된 쪽의 같은 칸 «완료 확인» 줄)을 «(○○ 작성)»으로 붙인다.
export function historyDetail(events, i) {
  const ev = events[i] || {};
  if (isDevStartedEvent(ev)) {
    // 라벨 «개발 시작일» 뒤 — 기록(옛 값 없음)/조정(옛 값 있음) · 칸 이름(dev_started_on)은 붙이지 않는다.
    const adjusted = ev.old !== null && ev.old !== undefined;
    return `${adjusted ? DEV_STARTED_ADJUSTED_TAIL : DEV_STARTED_RECORDED_TAIL} : ${text(ev.old)} → ${text(ev.new)}`;
  }
  if (ev.action === 'ai_screen' && ev.field === AI_SCREEN_FIELD) {
    return ` · ${AI_SCREEN_FIELD_LABEL} : ${aiScreenValue(ev.old)} → ${aiScreenValue(ev.new)}`;
  }
  const confirmLabel = isConfirmField(ev.field) ? CONFIRM_FIELD_LABELS[ev.field] : undefined;
  let field = '';
  if (ev.field && !['ask', 'qa_account'].includes(ev.action)) {
    field = confirmLabel || ev.field;
    if (confirmLabel && isConfirmClearEvent(ev) && !CONFIRM_NO_VALUE_FIELDS.includes(ev.field)) {
      // 그 값을 넣은 줄 = 더 오래된 쪽에서 가장 가까운, 같은 칸·new 가 비운 값과 같은 «완료 확인» 줄(값이 다르면 붙이지 않는다 — 지어내지 않는다).
      const wrote = events.slice(i + 1).find((x) => x?.action === 'confirm' && x?.field === ev.field && text(x?.new) === text(ev.old));
      if (wrote?.actor_name) field = `${field}(${wrote.actor_name} 작성)`;
    }
  }
  const hasValue = (ev.old !== null && ev.old !== undefined) || (ev.new !== null && ev.new !== undefined);
  const showValue = ev.action !== 'qa_account' && hasValue && !(confirmLabel && CONFIRM_NO_VALUE_FIELDS.includes(ev.field));
  return `${field ? ` · ${field}` : ''}${showValue ? ` : ${text(ev.old)} → ${text(ev.new)}` : ''}`;
}
